import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Row, Col, Button, message, Spin, Tag, Select, Space, Tooltip } from 'antd';
import { ReloadOutlined, FundOutlined, InfoCircleOutlined, RiseOutlined, FallOutlined } from '@ant-design/icons';
import * as echarts from 'echarts';
import { apiFetch } from '../utils/api.js';

// ===== 工具 =====
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const fmtYuan = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '¥0';
  const neg = n < 0;
  const abs = Math.abs(n);
  const s = abs >= 10000 ? (abs / 10000).toFixed(1) + '万' : abs.toLocaleString('zh-CN', { maximumFractionDigits: 0 });
  return (neg ? '-¥' : '¥') + s;
};
const fmtVal = (m) => {
  if (m.value == null || m.value === '') return '—';
  const v = Number(m.value);
  if (isNaN(v)) return '—';
  if (m.unit === '¥') return fmtYuan(v);
  if (m.unit === '%') return v.toFixed(1) + '%';
  if (m.unit === '次/年') return v.toFixed(1) + ' 次';
  if (m.unit === '倍') return v.toFixed(2) + ' 倍';
  if (m.unit === '天') return v.toFixed(0) + ' 天';
  return String(v);
};

// ===== 配色 =====
const CORAL = '#D85A30';
const INK = '#26201C';
const C = { blue: '#5B8FF9', green: '#3F8600', red: '#D85A30', amber: '#F5A623', purple: '#9270CA', teal: '#13C2C2', gray: '#bfbfbf' };
const LEVEL_META = {
  ok: { color: '#3F8600', bg: '#F6FFED', tag: 'success', label: '健康' },
  warn: { color: '#D48806', bg: '#FFF7E6', tag: 'warning', label: '关注' },
  bad: { color: '#CF1322', bg: '#FFF1F0', tag: 'error', label: '风险' },
  na: { color: '#bfbfbf', bg: '#FAFAFA', tag: 'default', label: '待录数' },
};

// ===== ECharts 封装 =====
function EChart({ option, height = 260 }) {
  const ref = useRef(null);
  const chartRef = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const c = echarts.init(ref.current);
    chartRef.current = c;
    const onResize = () => c.resize();
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); c.dispose(); chartRef.current = null; };
  }, []);
  useEffect(() => { if (chartRef.current && option) chartRef.current.setOption(option, true); }, [option]);
  return <div ref={ref} style={{ width: '100%', height }} />;
}

function BoardCard({ title, desc, children, style, extra }) {
  return (
    <Card size="small" style={{ borderRadius: 10, height: '100%', boxShadow: '0 1px 4px rgba(216,90,48,.06)', ...style }}
      styles={{ body: { padding: 16 } }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
        <div style={{ fontWeight: 600, fontSize: 15, color: INK }}>{title}</div>
        {extra}
      </div>
      {desc ? <div style={{ fontSize: 12, color: '#A08C82', marginBottom: 10, lineHeight: 1.6 }}>{desc}</div> : null}
      {children}
    </Card>
  );
}

// 单个指标行：名称 + 数值 + 健康度 + 参考，可展开解释/建议
function MetricRow({ m }) {
  const meta = LEVEL_META[m.level] || LEVEL_META.na;
  return (
    <div style={{ padding: '10px 12px', borderRadius: 8, border: `1px solid ${meta.color}26`, background: meta.bg, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>{m.name}</span>
        <span style={{ fontSize: 20, fontWeight: 700, color: meta.color, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtVal(m)}</span>
        <Tag color={meta.tag} style={{ borderRadius: 999, marginRight: 0 }}>{meta.label}</Tag>
        <span style={{ fontSize: 11, color: '#A08C82', marginLeft: 'auto' }}>参考：{m.ref}</span>
      </div>
      <div style={{ fontSize: 12, color: '#5F5E5A', marginTop: 5, lineHeight: 1.6 }}>{m.explain}</div>
      <div style={{ fontSize: 12, color: meta.color, marginTop: 3, lineHeight: 1.6 }}>
        <b>建议：</b>{m.advice}
      </div>
    </div>
  );
}

function MetricGroup({ title, color, list }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color, margin: '10px 0 8px', paddingLeft: 8, borderLeft: `3px solid ${color}` }}>{title}</div>
      {list.map((m) => <MetricRow key={m.key} m={m} />)}
    </div>
  );
}

export default function FinanceHealth() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const q = m ? '?month=' + encodeURIComponent(m) : '';
      const d = await apiFetch('/api/financial-health' + q).then(r => r.json());
      if (d.success) {
        setData(d.data);
        setMonth(d.data.month || null);
      } else message.error(d.error || '加载失败');
    } catch (e) { message.error('财务健康加载失败'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  const detail = data && data.detail;
  const trend = data && data.trend;
  const verdict = detail ? detail.verdict : data.verdict;
  const months = data && data.months ? data.months : [];

  const baseAxis = { axisLine: { lineStyle: { color: '#E8E0D8' } }, axisLabel: { color: '#A08C82', fontSize: 11 }, splitLine: { lineStyle: { color: '#F5EDE8' } } };
  const lineSeries = (name, arr, color, area = false) => ({ name, type: 'line', data: arr, smooth: true, itemStyle: { color }, lineStyle: { color }, ...(area ? { areaStyle: { color: color + '1a' } } : {}) });

  const optProfit = trend ? {
    color: [C.red, C.blue, C.purple],
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 10, right: 10, top: 24, bottom: 40, containLabel: true },
    xAxis: { type: 'category', data: trend.months, ...baseAxis },
    yAxis: { type: 'value', ...baseAxis, axisLabel: { ...baseAxis.axisLabel, formatter: v => v + '%' } },
    series: [
      lineSeries('毛利率', trend.series.grossMargin, C.red, true),
      lineSeries('净利率', trend.series.netMargin, C.blue, true),
      lineSeries('ROE', trend.series.roe, C.purple),
    ],
  } : null;

  const optSolvency = trend ? {
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 10, right: 10, top: 24, bottom: 40, containLabel: true },
    xAxis: { type: 'category', data: trend.months, ...baseAxis },
    yAxis: [
      { type: 'value', ...baseAxis, axisLabel: { ...baseAxis.axisLabel, formatter: v => v + '倍' } },
      { type: 'value', ...baseAxis, axisLabel: { ...baseAxis.axisLabel, formatter: v => v + '%' }, splitLine: { show: false } },
    ],
    series: [
      { ...lineSeries('流动比率', trend.series.currentRatio, C.blue), yAxisIndex: 0 },
      { ...lineSeries('速动比率', trend.series.quickRatio, C.teal), yAxisIndex: 0 },
      { ...lineSeries('资产负债率', trend.series.debtRatio, C.amber), yAxisIndex: 1 },
    ],
  } : null;

  const optOp = trend ? {
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 10, right: 10, top: 24, bottom: 40, containLabel: true },
    xAxis: { type: 'category', data: trend.months, ...baseAxis },
    yAxis: { type: 'value', ...baseAxis, axisLabel: { ...baseAxis.axisLabel, formatter: v => v + '天' } },
    series: [lineSeries('存货周转天数', trend.series.invDays, C.green, true)],
  } : null;

  // 杜邦三因子 + ROE
  const dp = detail && detail.dupont;
  const dupontItems = dp ? [
    { label: '净利率', val: dp.netMargin == null ? '—' : dp.netMargin.toFixed(1) + '%', desc: '每卖100元净赚多少', color: C.red },
    { label: '总资产周转率', val: dp.assetTurnover == null ? '—' : dp.assetTurnover.toFixed(2) + '次', desc: '1元资产带来几元收入', color: C.blue },
    { label: '权益乘数', val: dp.equityMultiplier == null ? '—' : dp.equityMultiplier.toFixed(2) + '倍', desc: '杠杆放大倍数', color: C.purple },
  ] : [];

  const kpis = detail ? [
    { label: month + ' 全渠道净入账', value: fmtYuan(detail.pl.netIncome) },
    { label: month + ' 全渠道净利润', value: fmtYuan(detail.pl.netProfit) },
    { label: '毛利率', value: dp && dp.netMargin != null ? (detail.pl.grossProfit / detail.pl.netIncome * 100).toFixed(1) + '%' : '—' },
    { label: 'ROE（净资产收益率）', value: dp && dp.roe != null ? dp.roe.toFixed(1) + '%' : '—' },
  ] : [];

  return (
    <div className="page-container">
      {/* 页头 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: INK }}>
            <FundOutlined style={{ color: CORAL, marginRight: 8 }} />财务健康
          </div>
          <div style={{ fontSize: 12, color: '#A08C82', marginTop: 4 }}>
            四大能力（偿债 / 营运 / 盈利 / 成长）+ 杜邦分析 + 现金流健康　|　专业会计视角，逐指标给出解释与优化建议
          </div>
        </div>
        <Space>
          <Select
            value={month} style={{ width: 130 }} placeholder="选择月份"
            options={months.map(m => ({ value: m, label: m }))}
            onChange={(m) => load(m)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => load(month)}>刷新</Button>
        </Space>
      </div>

      {/* 综合结论 */}
      {verdict && (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10, background: verdict.color, border: `1px solid ${verdict.border}`, fontSize: 14, color: INK, lineHeight: 1.7 }}>
          <b>综合判断（{month}）：</b>{verdict.text}
        </div>
      )}

      {/* KPI */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        {kpis.map((k, i) => (
          <Col xs={12} md={6} key={i}>
            <Card size="small" className="kpi-tap" style={{ borderRadius: 10, borderLeft: `3px solid ${CORAL}`, boxShadow: '0 1px 4px rgba(216,90,48,.06)' }} styles={{ body: { padding: '14px 16px' } }}>
              <div style={{ fontSize: 13, color: '#8C8C8C' }}>{k.label}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: INK, marginTop: 6, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{k.value}</div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 杜邦分析 */}
      {dp && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col span={24}>
            <BoardCard title="杜邦分析 · 赚钱来源拆解" desc={dp.note}>
              <Row gutter={[10, 10]} align="middle">
                {dupontItems.map((it, i) => (
                  <React.Fragment key={it.label}>
                    <Col xs={8} md={4}>
                      <div style={{ textAlign: 'center', border: `1px solid ${it.color}33`, background: it.color + '0f', borderRadius: 8, padding: '12px 8px' }}>
                        <div style={{ fontSize: 12, color: '#8C8C8C' }}>{it.label}</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: it.color, margin: '4px 0' }}>{it.val}</div>
                        <div style={{ fontSize: 11, color: '#A08C82' }}>{it.desc}</div>
                      </div>
                    </Col>
                    {i < 2 && <Col xs={2} md={1} style={{ textAlign: 'center', fontSize: 20, color: '#A08C82' }}>×</Col>}
                  </React.Fragment>
                ))}
                <Col xs={2} md={1} style={{ textAlign: 'center', fontSize: 20, color: '#A08C82' }}>=</Col>
                <Col xs={8} md={4}>
                  <div style={{ textAlign: 'center', border: '1px solid #3F860033', background: '#F6FFED', borderRadius: 8, padding: '12px 8px' }}>
                    <div style={{ fontSize: 12, color: '#8C8C8C' }}>ROE 净资产收益率</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#3F8600', margin: '4px 0' }}>{dp.roe == null ? '—' : dp.roe.toFixed(1) + '%'}</div>
                    <div style={{ fontSize: 11, color: '#A08C82' }}>老板投入的最终回报</div>
                  </div>
                </Col>
              </Row>
            </BoardCard>
          </Col>
        </Row>
      )}

      {/* 四大能力 + 现金流 */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <BoardCard title="偿债能力" desc="短期与长期还债的安全边际">
            <MetricGroup title="短期偿债" color={C.blue} list={detail ? detail.solvency.slice(0, 3) : []} />
            <MetricGroup title="长期偿债 / 杠杆" color={C.purple} list={detail ? detail.solvency.slice(3) : []} />
          </BoardCard>
        </Col>
        <Col xs={24} md={12}>
          <BoardCard title="营运能力" desc="资金与库存转得快不快（周转效率）">
            <MetricGroup title="周转效率" color={C.green} list={detail ? detail.operation : []} />
          </BoardCard>
        </Col>
        <Col xs={24} md={12}>
          <BoardCard title="盈利能力" desc="赚钱的质量与资产回报">
            <MetricGroup title="利润率与回报" color={C.red} list={detail ? detail.profitability : []} />
          </BoardCard>
        </Col>
        <Col xs={24} md={12}>
          <BoardCard title="成长能力" desc="收入与利润的环比走势">
            <MetricGroup title="环比增速" color={C.amber} list={detail ? detail.growth : []} />
            <div style={{ height: 10 }} />
            <BoardCard title="现金流健康" desc="真正可自由支配的现金与短期现金保障" style={{ boxShadow: 'none' }}>
              <MetricGroup title="现金流" color={C.teal} list={detail ? detail.cashflowMetrics : []} />
            </BoardCard>
          </BoardCard>
        </Col>
      </Row>

      {/* 趋势 */}
      {trend && trend.months.length > 0 && (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <BoardCard title="盈利质量趋势" desc="毛利率 / 净利率 / ROE 逐月变化">
                <EChart option={optProfit} height={260} />
              </BoardCard>
            </Col>
            <Col xs={24} md={12}>
              <BoardCard title="偿债与杠杆趋势" desc="流动比率 / 速动比率（左轴，倍）与资产负债率（右轴，%）">
                <EChart option={optSolvency} height={260} />
              </BoardCard>
            </Col>
          </Row>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <BoardCard title="营运效率趋势" desc="存货周转天数（越短越好）">
                <EChart option={optOp} height={260} />
              </BoardCard>
            </Col>
            <Col xs={24} md={12}>
              <BoardCard title="说明与口径" desc="指标数据来源（单一数据源）">
                <div style={{ fontSize: 13, color: '#5F5E5A', lineHeight: 2 }}>
                  <div>• <b>损益</b>（收入/成本/毛利/净利）为<b>全渠道</b>口径 = 拼多多（账务流水，到账）+ 微信客户（发货口径，净入账按已收款），与公司看板一致。</div>
                  <div>• <b>资产负债/现金流</b>来自「月末快照」，请在快照中按时实点填写。</div>
                  <div>• <b>周转类</b>平均余额 =（上月快照 + 本月快照）/ 2，无上月快照时用本月近似。</div>
                  <div>• <b>参考区间</b>为电商行业经验值，非绝对标准；判定用 <Tag color="success" style={{ borderRadius: 999 }}>健康</Tag><Tag color="warning" style={{ borderRadius: 999 }}>关注</Tag><Tag color="error" style={{ borderRadius: 999 }}>风险</Tag> 三档。</div>
                  <div>• <b>待录数</b>表示该指标因数据缺失无法计算，补录后自动点亮。</div>
                </div>
              </BoardCard>
            </Col>
          </Row>
        </>
      )}

      {(!detail || (!detail.hasLedger && !detail.hasFlow)) && (
        <Card size="small" style={{ borderRadius: 10, textAlign: 'center', padding: 24, background: '#FFF8F4' }}>
          <div style={{ fontSize: 14, color: '#5F5E5A' }}>
            暂无财务数据。请先到「财务管理 → 账务流水」导入账单，并在「公司看板 → 月末快照」补录资产负债与现金流，这里会自动算出完整财务健康度。
          </div>
        </Card>
      )}
    </div>
  );
}
